import { useState } from 'react';
import { updateCurrencyExchangeRate } from 'services/organization/currency.service';
import { ICurrencyTableItem } from 'types/currency.types';
import { formatShortDateAndHour } from 'utils/formatDatesAndHours';
import { displayErrorNotification, displaySuccessNotification } from 'utils/displayNudosStandardNotifications';
import CurrencyNameCell from '../currencyNameCell/CurrencyNameCell';
import CurrencyExchangeRateCell from '../currencyExchangeRateCell/CurrencyExchangeRateCell';
import { parseExchangeRateValue } from './currencyTableRow.utils';

import './currencyTableRow.scss';

interface ICurrencyTableRow {
  item: ICurrencyTableItem;
  onExchangeRateSaved?: () => Promise<unknown>;
}

/** Fila de la tabla de monedas. */
const CurrencyTableRow = ({ item, onExchangeRateSaved }: ICurrencyTableRow) => {
  const [baselineExchangeRate, setBaselineExchangeRate] = useState<number | null>(() =>
    parseExchangeRateValue(item.exchangeRate)
  );
  const [currentExchangeRate, setCurrentExchangeRate] = useState<number | null>(() =>
    parseExchangeRateValue(item.exchangeRate)
  );
  const [isSaving, setIsSaving] = useState(false);

  const hasExchangeRateChanged = currentExchangeRate !== baselineExchangeRate;
  const isSaveEnabled = hasExchangeRateChanged && currentExchangeRate !== null;

  const updatedBy = !item.updatedBy ? '—' : item.updatedBy;
  const { textWithTimezone } = formatShortDateAndHour(item.updatedAt ?? '');
  const lastUpdate = textWithTimezone ?? '';

  /** Actualiza el estado local de la tasa de cambio. */
  const handleExchangeRateChange = (value: number | null) => {
    setCurrentExchangeRate(value);
  };

  /** Persiste la tasa de cambio editada. */
  const handleSaveCurrency = async () => {
    if (!isSaveEnabled || currentExchangeRate === null) {
      return;
    }

    setIsSaving(true);

    try {
      await updateCurrencyExchangeRate(item.id, { exchangeRate: currentExchangeRate });
      setBaselineExchangeRate(currentExchangeRate);
      await onExchangeRateSaved?.();
      displaySuccessNotification({ customJSXMessage: <>Se guardó la información correctamente.</> });
    } catch {
      displayErrorNotification();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <tr className="currencyTableRow">
      <td className="currencyNameColumn">
        <div className="currencyRowInputZone currencyRowInputZoneStart">
          <CurrencyNameCell flag={item.flag} name={item.name} />
        </div>
      </td>

      <td className="exchangeRateColumn">
        <CurrencyExchangeRateCell
          code={item.code}
          value={currentExchangeRate}
          onChange={handleExchangeRateChange}
        />
      </td>

      <td className="lastUpdateColumn">
        <div className="currencyRowInputZone currencyRowInputZoneStart">{lastUpdate}</div>
      </td>

      <td className="updatedByColumn">
        <div className="currencyRowInputZone currencyRowInputZoneStart">{updatedBy}</div>
      </td>

      <td className="actionsColumn">
        <div className="currencyRowInputZone">
          <button
            className="saveCurrencyButton"
            type="button"
            disabled={!isSaveEnabled || isSaving}
            onClick={handleSaveCurrency}
          >
            {isSaving ? 'Loading' : 'Guardar'}
          </button>
        </div>
      </td>
    </tr>
  );
};

export default CurrencyTableRow;
